import React from 'react';
import Icon from '../../../components/AppIcon';

const MarketCoverageAnalysis = ({ territoryData, capacityData, countries }) => {
  const getCoverageStatus = (supplierCount) => {
    if (supplierCount === 0) return { status: 'Aucune couverture', color: 'text-red-600', bgColor: 'bg-red-100', icon: 'AlertCircle' };
    if (supplierCount <= 2) return { status: 'Couverture faible', color: 'text-yellow-600', bgColor: 'bg-yellow-100', icon: 'AlertTriangle' };
    if (supplierCount <= 5) return { status: 'Couverture moyenne', color: 'text-blue-600', bgColor: 'bg-blue-100', icon: 'Info' };
    return { status: 'Couverture optimale', color: 'text-green-600', bgColor: 'bg-green-100', icon: 'CheckCircle' };
  };

  const coverageGaps = countries?.filter(country => {
    const territory = territoryData?.find(t => t?.countryId === country?.id);
    return !territory || territory?.suppliers?.length === 0;
  });

  const expansionOpportunities = territoryData?.filter(t => t?.suppliers?.length > 0 && t?.suppliers?.length <= 2);

  return (
    <div className="space-y-6">
      {/* Coverage Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Marchés Couverts</h3>
            <Icon name="Globe" className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{territoryData?.filter(t => t?.suppliers?.length > 0)?.length}</p>
          <p className="text-sm text-gray-600 mt-1">sur {countries?.length} pays actifs</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Lacunes de Couverture</h3>
            <Icon name="AlertCircle" className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{coverageGaps?.length}</p>
          <p className="text-sm text-gray-600 mt-1">pays sans fournisseur</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Opportunités d'Expansion</h3>
            <Icon name="TrendingUp" className="w-8 h-8 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{expansionOpportunities?.length}</p>
          <p className="text-sm text-gray-600 mt-1">marchés à renforcer</p>
        </div>
      </div>

      {/* Coverage Status by Market */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">État de la Couverture par Marché</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pays</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fournisseurs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Machines</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action Recommandée</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {capacityData?.map(capacity => {
                const coverage = getCoverageStatus(capacity?.supplierCount);
                return (
                  <tr key={capacity?.countryId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icon name="MapPin" className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{capacity?.countryName}</div>
                          <div className="text-sm text-gray-500">{capacity?.countryCode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{capacity?.supplierCount}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{capacity?.machineCount}</div>
                      <div className="text-xs text-gray-500">{capacity?.averageMachinesPerSupplier} par fournisseur</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${coverage?.bgColor} ${coverage?.color}`}>
                        <Icon name={coverage?.icon} className="w-4 h-4" />
                        {coverage?.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {capacity?.supplierCount === 0 && 'Assigner des fournisseurs'}
                      {capacity?.supplierCount > 0 && capacity?.supplierCount <= 2 && 'Ajouter des fournisseurs'}
                      {capacity?.supplierCount > 2 && capacity?.supplierCount <= 5 && 'Optimiser la capacité'}
                      {capacity?.supplierCount > 5 && 'Maintenir la couverture'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Coverage Gaps */}
      {coverageGaps?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Lacunes de Couverture Identifiées</h3>
              <p className="text-sm text-red-700 mb-3">Les pays suivants n'ont aucun fournisseur assigné :</p>
              <div className="flex flex-wrap gap-2">
                {coverageGaps?.map(country => (
                  <span key={country?.id} className="px-3 py-1 bg-white text-red-700 text-sm font-medium rounded border border-red-200">
                    {country?.countryName} ({country?.countryCode})
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expansion Opportunities */}
      {expansionOpportunities?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="TrendingUp" className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Opportunités d'Expansion</h3>
              <p className="text-sm text-blue-700 mb-3">Ces marchés ont une couverture limitée et pourraient bénéficier de fournisseurs supplémentaires :</p>
              <div className="space-y-2">
                {expansionOpportunities?.map(territory => (
                  <div key={territory?.countryId} className="flex items-center justify-between p-3 bg-white rounded border border-blue-200">
                    <div>
                      <span className="font-medium text-blue-900">{territory?.countryName}</span>
                      <span className="text-sm text-blue-700 ml-2">({territory?.suppliers?.length} fournisseurs, {territory?.totalMachines} machines)</span>
                    </div>
                    <button className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-100 rounded transition-colors">
                      Voir détails
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketCoverageAnalysis;